import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Download, User, CreditCard, Building, FileText } from 'lucide-react';
import { getOnboarding, getPublicShare } from '../utils/api';
import jsPDF from 'jspdf';
import { RUBICON_LOGO_BASE64 } from '../utils/pdfAssets';
import NonIndividualView from './NonIndividualView';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;


const ViewOnboarding = ({ publicToken = null }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetcher = publicToken
      ? getPublicShare(publicToken).then((r) => r.data)
      : getOnboarding(id);
    fetcher
      .then((data) => { if (data) setRecord(data); })
      .catch(() => {
        if (!publicToken) {
          alert('Onboarding record not found');
          navigate('/admin/onboarding');
        }
      })
      .finally(() => setLoading(false));
  }, [id, publicToken, navigate]);

    const downloadPDF = async () => {
    if (!record) {
      alert('No record data available');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPos = 15;

    // Helper function to convert PDF page to image
    const pdfToImage = async (pdfData) => {
      try {
        const loadingTask = pdfjsLib.getDocument(pdfData);
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1); // Get first page
        
        const scale = 2.0;
        const viewport = page.getViewport({ scale });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
        
        return canvas.toDataURL('image/jpeg', 0.8);
      } catch (error) {
        console.error('Error converting PDF to image:', error);
        return null;
      }
    };

    // Helper function to add document image or PDF
    const addDocumentImage = async (documentData, yPosition) => {
      let newYPos = yPosition;
      
      if (documentData.startsWith('data:image')) {
        // Image document
        const imgWidth = 150;
        const imgHeight = 90;
        doc.addImage(documentData, 'JPEG', (pageWidth - imgWidth) / 2, newYPos, imgWidth, imgHeight);
      } else if (documentData.startsWith('data:application/pdf')) {
        // PDF document - convert first page to image
        const imageData = await pdfToImage(documentData);
        
        if (imageData) {
          const imgWidth = 150;
          const imgHeight = 90;
          doc.addImage(imageData, 'JPEG', (pageWidth - imgWidth) / 2, newYPos, imgWidth, imgHeight);
        } else {
          // Fallback if conversion fails
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(200, 0, 0);
          doc.text('⚠ Unable to display PDF content', pageWidth / 2, newYPos, { align: 'center' });
          newYPos += 8;
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text('Please upload as image (JPG/PNG) for preview', pageWidth / 2, newYPos, { align: 'center' });
        }
      } else {
        // Other format
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text('Document attached', pageWidth / 2, newYPos, { align: 'center' });
      }
      
      return newYPos;
    };

    // Footer function
    const addFooter = () => {
      const footerY = pageHeight - 25;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('6A Dr.Rajendra Prasad Sarani, 4th Floor, R.No-20, Kolkata-700001', pageWidth / 2, footerY, { align: 'center' });
      doc.text('Email: communications@rubiconfinservices.com | Tel: +91-33-40442577', pageWidth / 2, footerY + 4, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    };

    // Add logo
    try {
      const logoWidth = 50;
      const logoHeight = 48;
      doc.addImage(RUBICON_LOGO_BASE64, 'JPEG', (pageWidth - logoWidth) / 2, yPos, logoWidth, logoHeight);
      yPos += 52;
    } catch (error) {
      console.error('Error adding logo:', error);
      yPos += 20;
    }

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('INVESTOR ONBOARDING FORM', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('KYC & Account Opening Documentation', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    // Personal Information
    doc.setFillColor(240, 249, 255);
    doc.rect(15, yPos, pageWidth - 30, 60, 'F');
    yPos += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('PERSONAL INFORMATION', 20, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Name: ${record.name}`, 20, yPos);
    yPos += 6;
    doc.text(`PAN: ${record.pan}`, 20, yPos);
    doc.text(`Email: ${record.email}`, 120, yPos);
    yPos += 6;
    doc.text(`Date of Birth: ${record.dob ? new Date(record.dob).toLocaleDateString() : 'N/A'}`, 20, yPos);
    doc.text(`Mobile: ${record.mobile}`, 120, yPos);
    yPos += 6;
    doc.text(`Father's Name: ${record.father_name}`, 20, yPos);
    yPos += 6;
    doc.text(`Mother's Name: ${record.mother_name}`, 20, yPos);
    yPos += 6;
    const addressLines = doc.splitTextToSize(`Address: ${record.address}`, pageWidth - 40);
    doc.text(addressLines, 20, yPos);
    yPos += addressLines.length * 5 + 10;

    // Bank Details
    doc.setFillColor(240, 253, 244);
    doc.rect(15, yPos, pageWidth - 30, 35, 'F');
    yPos += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('BANK DETAILS', 20, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Bank Name: ${record.bank_name}`, 20, yPos);
    yPos += 6;
    doc.text(`Account Number: ${record.account_no}`, 20, yPos);
    yPos += 6;
    doc.text(`IFSC Code: ${record.ifsc_code}`, 20, yPos);
    doc.text(`MICR Number: ${record.micr_no}`, 120, yPos);
    yPos += 12;

    // Nominee Details
    doc.setFillColor(255, 247, 237);
    doc.rect(15, yPos, pageWidth - 30, 50, 'F');
    yPos += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('NOMINEE DETAILS', 20, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Nominee Name: ${record.nominee_name}`, 20, yPos);
    yPos += 6;
    doc.text(`Relationship: ${record.nominee_relationship}`, 20, yPos);
    doc.text(`Date of Birth: ${new Date(record.nominee_dob).toLocaleDateString()}`, 120, yPos);
    yPos += 6;
    doc.text(`ID Type: ${record.nominee_id_type === 'pan' ? 'PAN' : 'Aadhar'}`, 20, yPos);
    doc.text(`ID Number: ${record.nominee_id_number}`, 120, yPos);
    yPos += 6;
    doc.text(`Mobile: ${record.nominee_mobile}`, 20, yPos);
    doc.text(`Email: ${record.nominee_email}`, 120, yPos);
    yPos += 6;
    const nomineeAddressLines = doc.splitTextToSize(`Address: ${record.nominee_address}`, pageWidth - 40);
    doc.text(nomineeAddressLines, 20, yPos);
    yPos += nomineeAddressLines.length * 5 + 10;

    // Add photo on separate page
    if (record.photo) {
      addFooter();
      doc.addPage();
      yPos = 20;
      
      try {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('INVESTOR PHOTOGRAPH', pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;
        
        // Center the photo
        const photoWidth = 60;
        const photoHeight = 60;
        doc.addImage(record.photo, 'JPEG', (pageWidth - photoWidth) / 2, yPos, photoWidth, photoHeight);
      } catch (error) {
        console.error('Error adding photo:', error);
      }
    }

    // PAN Proof on separate page
    if (record.pan_proof) {
      addFooter();
      doc.addPage();
      yPos = 20;
      
      try {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('PAN CARD PROOF', pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;
        
        await addDocumentImage(record.pan_proof, yPos);
      } catch (error) {
        console.error('Error adding PAN proof:', error);
      }
    }

    // Aadhar Proof on separate page
    if (record.aadhar_proof) {
      addFooter();
      doc.addPage();
      yPos = 20;
      
      try {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('AADHAR CARD PROOF', pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;
        
        await addDocumentImage(record.aadhar_proof, yPos);
      } catch (error) {
        console.error('Error adding Aadhar proof:', error);
      }
    }

    // Bank Proof on separate page
    if (record.bank_proof) {
      addFooter();
      doc.addPage();
      yPos = 20;

      try {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('BANK PROOF (CANCELLED CHEQUE/STATEMENT)', pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;
        
        await addDocumentImage(record.bank_proof, yPos);
      } catch (error) {
        console.error('Error adding bank proof:', error);
      }
    }

    // Signature on separate page
    if (record.signature) {
      addFooter();
      doc.addPage();
      yPos = 20;
      
      try {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('INVESTOR SIGNATURE', pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;
        
        const sigWidth = 80;
        const sigHeight = 40;
        doc.addImage(record.signature, 'JPEG', (pageWidth - sigWidth) / 2, yPos, sigWidth, sigHeight);
      } catch (error) {
        console.error('Error adding signature:', error);
      }
    }

    // NRI specific documents
    if (record.investor_type === 'nri') {
      // Passport Copy
      if (record.passport_copy) {
        addFooter();
        doc.addPage();
        yPos = 20;
        
        try {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text('PASSPORT COPY', pageWidth / 2, yPos, { align: 'center' });
          yPos += 10;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text(`Passport Number: ${record.passport_number || 'N/A'}`, pageWidth / 2, yPos, { align: 'center' });
          yPos += 10;
          
          await addDocumentImage(record.passport_copy, yPos);
        } catch (error) {
          console.error('Error adding passport copy:', error);
        }
      }

      // PIS Permission Letter
      if (record.pis_letter) {
        addFooter();
        doc.addPage();
        yPos = 20;
        
        try {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text('PIS PERMISSION LETTER', pageWidth / 2, yPos, { align: 'center' });
          yPos += 15;
          
          await addDocumentImage(record.pis_letter, yPos);
        } catch (error) {
          console.error('Error adding PIS letter:', error);
        }
      }
    }

    // Minor specific documents
    if (record.investor_type === 'minor') {
      // Guardian Address Proof
      if (record.guardian_address_proof) {
        addFooter();
        doc.addPage();
        yPos = 20;
        
        try {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text('GUARDIAN ADDRESS PROOF', pageWidth / 2, yPos, { align: 'center' });
          yPos += 10;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text(`Guardian Name: ${record.guardian_name || 'N/A'}`, pageWidth / 2, yPos, { align: 'center' });
          yPos += 5;
          doc.text(`Guardian PAN: ${record.guardian_pan || 'N/A'}`, pageWidth / 2, yPos, { align: 'center' });
          yPos += 10;
          
          addDocumentImage(record.guardian_address_proof, yPos);
        } catch (error) {
          console.error('Error adding guardian address proof:', error);
        }
      }

      // Birth Certificate
      if (record.birth_certificate) {
        addFooter();
        doc.addPage();
        yPos = 20;
        
        try {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text('BIRTH CERTIFICATE', pageWidth / 2, yPos, { align: 'center' });
          yPos += 15;
          
          addDocumentImage(record.birth_certificate, yPos);
        } catch (error) {
          console.error('Error adding birth certificate:', error);
        }
      }
    }

    // Declaration on new page
    addFooter();
    doc.addPage();
    yPos = 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DECLARATION', 20, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const declaration = 'I hereby declare that all the information provided above is true and correct to the best of my knowledge. I authorize Rubicon Finservices to verify the details and process my account opening application.';
    const declarationLines = doc.splitTextToSize(declaration, pageWidth - 40);
    doc.text(declarationLines, 20, yPos);
    yPos += declarationLines.length * 5 + 15;

    doc.text(`Date: ${new Date(record.created_at).toLocaleDateString()}`, 20, yPos);
    doc.text('Signature: _____________________', 120, yPos);

    // Add footer
    addFooter();

    // Save PDF
    doc.save(`Onboarding_${record.name.replace(/\s+/g, '_')}_${record.pan}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again. Error: ' + error.message);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!record) {
    return <div className="min-h-screen flex items-center justify-center">Record not found</div>;
  }

  if (record.client_type === 'non_individual') {
    return <NonIndividualView record={record} publicToken={publicToken} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          {!publicToken ? (
            <Button
              variant="outline"
              onClick={() => navigate('/admin/onboarding')}
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Onboarding List
            </Button>
          ) : <div />}
          <Button
            onClick={downloadPDF}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download size={20} className="mr-2" />
            Download PDF
          </Button>
        </div>

        {/* Personal Information */}
        <Card className="mb-6">
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-2xl flex items-center gap-2">
              <User size={24} />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-600">Full Name</p>
                <p className="font-semibold text-slate-900 text-lg">{record.name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">PAN</p>
                <p className="font-semibold text-slate-900 text-lg">{record.pan}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Date of Birth</p>
                <p className="font-semibold text-slate-900 text-lg">{record.dob ? new Date(record.dob).toLocaleDateString() : 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Email</p>
                <p className="font-semibold text-slate-900">{record.email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Mobile</p>
                <p className="font-semibold text-slate-900">{record.mobile}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Father's Name</p>
                <p className="font-semibold text-slate-900">{record.father_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Mother's Name</p>
                <p className="font-semibold text-slate-900">{record.mother_name}</p>
              </div>
              <div className="md:col-span-3">
                <p className="text-sm text-slate-600">Address</p>
                <p className="font-semibold text-slate-900">{record.address}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank Details */}
        <Card className="mb-6">
          <CardHeader className="bg-green-50">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Building size={24} />
              Bank Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-slate-600">Bank Name</p>
                <p className="font-semibold text-slate-900 text-lg">{record.bank_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Account Number</p>
                <p className="font-semibold text-slate-900 text-lg">{record.account_no}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">IFSC Code</p>
                <p className="font-semibold text-slate-900">{record.ifsc_code}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">MICR Number</p>
                <p className="font-semibold text-slate-900">{record.micr_no}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Photo and Documents */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {/* Photo */}
          {record.photo && (
            <Card>
              <CardHeader className="bg-purple-50">
                <CardTitle className="text-lg">Investor Photo</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <img
                  src={record.photo}
                  alt="Investor"
                  className="w-full h-64 object-cover rounded-lg border-2 border-purple-200"
                />
              </CardContent>
            </Card>
          )}

          {/* PAN Proof */}
          {record.pan_proof && (
            <Card>
              <CardHeader className="bg-amber-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard size={20} />
                  PAN Proof
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {record.pan_proof.startsWith('data:image') ? (
                  <img
                    src={record.pan_proof}
                    alt="PAN Proof"
                    className="w-full h-48 object-contain rounded-lg border-2 border-amber-200"
                  />
                ) : (
                  <div className="flex items-center justify-center h-48 bg-amber-100 rounded-lg">
                    <FileText size={48} className="text-amber-600" />
                    <p className="ml-2 text-amber-700">PDF Document</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Bank Proof */}
          {record.bank_proof && (
            <Card>
              <CardHeader className="bg-green-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText size={20} />
                  Bank Proof
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {record.bank_proof.startsWith('data:image') ? (
                  <img
                    src={record.bank_proof}
                    alt="Bank Proof"
                    className="w-full h-48 object-contain rounded-lg border-2 border-green-200"
                  />
                ) : (
                  <div className="flex items-center justify-center h-48 bg-green-100 rounded-lg">
                    <FileText size={48} className="text-green-600" />
                    <p className="ml-2 text-green-700">PDF Document</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Onboarding Date */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-slate-600">Onboarding Date</p>
                <p className="font-semibold text-slate-900">{new Date(record.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Record ID</p>
                <p className="font-mono text-xs text-slate-700">{record.id}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ViewOnboarding;
